// src/pages/HomePage.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { tripService, articleService, UPLOADS_BASE_URL } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FaClock, FaEye, FaMapMarkerAlt, FaCalendarAlt, FaUsers } from 'react-icons/fa';
import SEO from '../components/SEO';
import './HomePage.css';

const HomePage = () => {
  const { currentUser } = useAuth();
  const [featuredTrips, setFeaturedTrips] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);

  useEffect(() => {
    fetchFeaturedContent();
  }, []);

  const fetchFeaturedContent = async () => {
    try {
      // Fetch featured articles
      const articlesResponse = await articleService.getFeaturedArticles(3);
      setFeaturedArticles(articlesResponse.data);

      // Fetch upcoming trips
      const tripsResponse = await tripService.getAllTrips();
      const upcomingTrips = tripsResponse.data
        .filter(trip => trip.status === 'upcoming')
        .slice(0, 6);
      setFeaturedTrips(upcomingTrips);
    } catch (error) {
      console.error('Error fetching featured content:', error);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateSpotsLeft = (trip) => {
    const approved = trip.applications?.filter(app => app.status === 'approved').length || 0;
    return trip.maxParticipants - approved;
  };

  return (
    <div className="homepage">
      <SEO 
        title="DiveConnect - Discover Amazing Scuba Diving Trips Worldwide"
        description="Book unforgettable scuba diving trips to the world's best dive sites. Expert-led adventures in the Maldives, Red Sea, Indonesia, and more. Browse trips, read diving guides, and join our community."
        keywords="scuba diving trips, dive vacations, diving holidays, dive sites, underwater adventure, diving tours, scuba travel, liveaboard diving, dive packages, diving destinations"
        url="https://dive-connect.com"
      />
      
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-overlay"></div>
        <Container>
          <Row className="hero-content">
            <Col md={10} lg={8} className="mx-auto text-center">
              <h1 className="hero-title">Discover the Ocean's Depths</h1>
              <p className="hero-subtitle">
                Join our community of passionate divers. Explore amazing underwater sites, 
                learn from experts, and create unforgettable memories.
              </p>
              {currentUser ? (
                <div className="hero-buttons">
                  <Link to="/trips">
                    <Button size="lg" variant="primary" className="me-3">
                      Browse Dive Trips
                    </Button>
                  </Link>
                  <Link to="/blog">
                    <Button size="lg" variant="outline-light">
                      Read Diving Guides
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="hero-buttons">
                  <Link to="/register">
                    <Button size="lg" variant="primary" className="me-3">
                      Join Now - It's Free
                    </Button>
                  </Link>
                  <Link to="/blog">
                    <Button size="lg" variant="outline-light">
                      Explore Blog
                    </Button>
                  </Link>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      {/* Featured Articles Section */}
      <section className="featured-articles-section">
        <Container>
          <div className="section-header text-center">
            <h2 className="section-title">Latest Diving Insights</h2>
            <p className="section-subtitle">
              Expert guides, destination reviews, and diving tips from our community
            </p>
          </div>

          <Row className="mt-5">
            {featuredArticles.length > 0 ? (
              featuredArticles.map((article) => (
                <Col key={article._id} md={6} lg={4} className="mb-4">
                  <Card className="article-card h-100 shadow-sm">
                    <Card.Img
                      variant="top"
                      src={`${UPLOADS_BASE_URL}/uploads/${article.featuredImage}`}
                      alt={article.title}
                      className="article-card-img"
                      onError={(e) => {
                        e.target.src = `${UPLOADS_BASE_URL}/uploads/default-article.jpg`;
                      }}
                    />
                    <Card.Body className="d-flex flex-column">
                      <div className="mb-2">
                        <Badge bg="info" className="article-badge">
                          {article.category.replace('-', ' ')}
                        </Badge>
                      </div>
                      <Card.Title className="article-card-title">
                        <Link to={`/blog/${article.slug}`} className="text-dark text-decoration-none stretched-link">
                          {article.title}
                        </Link>
                      </Card.Title>
                      <Card.Text className="text-muted flex-grow-1">
                        {article.excerpt}
                      </Card.Text>
                      <div className="article-meta d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          <FaClock className="me-1" />
                          {article.readTime} min read
                        </small>
                        <small className="text-muted">
                          <FaEye className="me-1" />
                          {article.views} views
                        </small>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            ) : (
              <Col className="text-center">
                <p className="text-muted">No articles yet. Check back soon!</p>
              </Col>
            )}
          </Row>

          <div className="text-center mt-4">
            <Link to="/blog">
              <Button variant="outline-primary" size="lg">
                View All Articles
              </Button>
            </Link>
          </div>
        </Container>
      </section>

      {/* Featured Trips Section */}
      <section className="featured-trips-section">
        <Container>
          <div className="section-header text-center">
            <h2 className="section-title">Upcoming Dive Trips</h2>
            <p className="section-subtitle">
              Join our organized diving adventures to the world's best dive sites
            </p>
          </div>

          <Row className="mt-5">
            {featuredTrips.length > 0 ? (
              featuredTrips.map((trip) => {
                const spotsLeft = calculateSpotsLeft(trip);
                const imageUrl = trip.images && trip.images.length > 0 
                  ? trip.images[0] 
                  : trip.image || 'default-trip.jpg';

                return (
                  <Col key={trip._id} md={6} lg={4} className="mb-4">
                    <Card className="trip-card h-100 shadow-sm">
                      <Link to={`/trips/${trip._id}`}>
                        <Card.Img
                          variant="top"
                          src={`${UPLOADS_BASE_URL}/uploads/${imageUrl}`}
                          alt={trip.name}
                          className="trip-card-img"
                          onError={(e) => {
                            e.target.src = `${UPLOADS_BASE_URL}/uploads/default-trip.jpg`;
                          }}
                        />
                      </Link>
                      {spotsLeft <= 3 && spotsLeft > 0 && (
                        <div className="spots-badge">
                          Only {spotsLeft} spots left!
                        </div>
                      )}
                      {spotsLeft === 0 && (
                        <div className="spots-badge full">
                          Fully Booked
                        </div>
                      )}
                      <Card.Body className="d-flex flex-column">
                        <Card.Title className="trip-card-title">
                          <Link to={`/trips/${trip._id}`} className="text-dark text-decoration-none">
                            {trip.name}
                          </Link>
                        </Card.Title>
                        
                        <div className="trip-info mb-3">
                          <div className="trip-info-item">
                            <FaMapMarkerAlt className="me-2 text-primary" />
                            <span>{trip.location}</span>
                          </div>
                          <div className="trip-info-item">
                            <FaCalendarAlt className="me-2 text-primary" />
                            <span>{formatDate(trip.startDate)}</span>
                          </div>
                          <div className="trip-info-item">
                            <FaUsers className="me-2 text-primary" />
                            <span>{spotsLeft} / {trip.maxParticipants} spots available</span>
                          </div>
                          {trip.price && (
                            <div className="trip-price mt-2">
                              <strong>{trip.currency || 'EUR'} {trip.price}</strong>
                            </div>
                          )}
                        </div>

                        <Link to={`/trips/${trip._id}`} className="mt-auto">
                          <Button variant="primary" className="w-100">
                            View Details
                          </Button>
                        </Link>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })
            ) : (
              <Col className="text-center">
                <p className="text-muted">No upcoming trips at the moment. Check back soon!</p>
              </Col>
            )}
          </Row>

          <div className="text-center mt-4">
            <Link to="/trips">
              <Button variant="outline-primary" size="lg">
                View All Trips
              </Button>
            </Link>
          </div>
        </Container>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="section-title">Why Dive With Us?</h2>
          </div>

          <Row className="text-center">
            <Col md={3} className="mb-4">
              <div className="feature-icon">🏆</div>
              <h4>Expert Guides</h4>
              <p className="text-muted">
                Dive with certified professionals who know the best spots
              </p>
            </Col>
            <Col md={3} className="mb-4">
              <div className="feature-icon">🌊</div>
              <h4>Amazing Locations</h4>
              <p className="text-muted">
                Explore the world's most beautiful dive sites
              </p>
            </Col>
            <Col md={3} className="mb-4">
              <div className="feature-icon">👥</div>
              <h4>Great Community</h4>
              <p className="text-muted">
                Meet like-minded divers and make lifelong friends
              </p>
            </Col>
            <Col md={3} className="mb-4">
              <div className="feature-icon">✅</div>
              <h4>Safe & Fun</h4>
              <p className="text-muted">
                Safety first, with equipment checks and buddy systems
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Container>
          <Row>
            <Col md={8} className="mx-auto text-center">
              <h2 className="text-white mb-3">Ready for Your Next Adventure?</h2>
              <p className="text-white mb-4">
                Join thousands of divers discovering the underwater world
              </p>
              {!currentUser && (
                <Link to="/register">
                  <Button size="lg" variant="light">
                    Create Free Account
                  </Button>
                </Link>
              )}
              {currentUser && (
                <Link to="/trips">
                  <Button size="lg" variant="light">
                    Browse All Trips
                  </Button>
                </Link>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default HomePage;
